<script setup lang="ts">
/**
 * Alert turned off (Figma `3603:68668`).
 *
 * Opened from "Turn off this alert" in an alert email: a signed link with no
 * expiry, works without signing in, also for guests. Undo turns it back on;
 * View item opens the product page.
 */
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { FairButton } from '@/ui/primitives/FairButton'
import { useFairToast } from '@/ui/patterns/FairToast'
import { useAuthModal } from '@/features/auth/store'
import AuthScreenShell from '@/features/auth/ui/parts/AuthScreenShell.vue'
import successUrl from '@/assets/img/auth/illustration-password-updated.png'
import { turnOnAlert } from '../api/emailLinks'
import { isEmailLinksActionAvailable } from '../flags'
import { useEmailLinkStore } from '../store'

const { t } = useI18n()
const router = useRouter()
const toast = useFairToast()
const authModal = useAuthModal()
const links = useEmailLinkStore()

const undoAvailable = isEmailLinksActionAvailable('alert_undo')
const undoing = ref(false)
const undone = ref(false)

function viewItem() {
  const url = links.alertProductUrl
  if (!url) return
  authModal.hideAuthModal()
  const target = new URL(url, window.location.origin)
  if (target.origin === window.location.origin) {
    router.push(target.pathname + target.search + target.hash)
  } else {
    window.location.assign(target.href)
  }
}

async function undo() {
  undoing.value = true
  try {
    await turnOnAlert(links.alertToken)
    undone.value = true
    toast.show({ message: t('email_links.toast.alert_back_on') })
  } catch {
    toast.show({ message: t('email_links.errors.generic'), tone: 'warning' })
  } finally {
    undoing.value = false
  }
}
</script>

<template>
  <AuthScreenShell
    centered
    :title="t('email_links.alert_off.title')"
    :description="t('email_links.alert_off.description')"
  >
    <template #art>
      <img
        class="fair-auth-screen__art"
        :src="successUrl"
        alt=""
      >
    </template>

    <div class="fair-auth-actions">
      <FairButton
        v-if="links.alertProductUrl"
        class="fair-auth-cta"
        size="auto"
        :label="t('email_links.alert_off.view_item')"
        @click="viewItem"
      />
      <FairButton
        v-else
        class="fair-auth-cta"
        size="auto"
        :label="t('email_links.continue')"
        @click="authModal.hideAuthModal()"
      />
      <FairButton
        v-if="undoAvailable && !undone"
        class="fair-auth-cta"
        size="auto"
        variant="tonal"
        :label="t('email_links.undo')"
        :loading="undoing"
        @click="undo"
      />
    </div>
  </AuthScreenShell>
</template>
